import { AccessLimitGateService } from '@modules/settings/services/access-limit-gate.service';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Injector,
  signal,
  type TemplateRef,
  viewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { GoogleAnalyticsService } from '@frontendlib/common/services/google-analytics.service';
import { ToasterService } from '@frontendlib/common/features/toaster/services/toaster.service';
import { Button2Component } from '@frontendlib/components/components/button/button2.component';
import { IconComponent } from '@frontendlib/components/components/icon/icon.component';
import { ContextMenuItemComponent } from '@frontendlib/components/features/popover/components/context-menu-item/context-menu-item.component';
import { ContextMenuWindowComponent } from '@frontendlib/components/features/popover/components/context-menu-window/context-menu-window.component';
import { PopoverDirective } from '@frontendlib/components/features/popover/directives/popover.directive';
import { Application } from '@modules/common/types/application';
import { AdvancedSearchService } from '@modules/search/services/advanced-search.service';
import { ImportDialogComponent } from '@modules/sheets/import-dialog.component';
import {
  blankWorkbook,
  createAndOpenWorkbook,
  createFromTemplateAndOpen,
  importAndOpenWorkbook,
  type LibraryView,
  type TemplateChoice,
  openInEditorFullWindow,
} from '@modules/sheets/new-workbook';
import { injectFullWindowStart } from '@modules/common/services/full-window.service';
import { SheetsService } from '@modules/sheets/services/sheets.service';
import { SheetsAppStateService } from '@modules/sheets/services/state.service';
import { SHEETS_STRINGS } from '@sharedlib/sheets-editor/sheets.strings';
import { TemplateChooserComponent } from '@modules/sheets/template-chooser.component';
import { StitchType } from '@sharedlib/common/types/StitchType';
import { Subject } from 'rxjs';
import { NavAddMenuComponent } from '../nav-add-menu/nav-add-menu.component';

/**
 * Sheets' actions in the collapsible rail (settings report S-26): the New Workbook menu and the global Add menu,
 * handed to the shell as `[actionsTemplate]` the way Slides does. It replaces the top bar's New Workbook button.
 * The host is `display: contents` rather than hidden, because the Import and template dialogs render inside it.
 */
@Component({
  selector: 'app-sheets-rail-controls',
  templateUrl: './sheets-rail-controls.component.html',
  styleUrl: './sheets-rail-controls.component.less',
  host: { class: 'contents' },
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    Button2Component,
    ContextMenuItemComponent,
    ContextMenuWindowComponent,
    IconComponent,
    ImportDialogComponent,
    NavAddMenuComponent,
    PopoverDirective,
    TemplateChooserComponent,
  ],
})
export class SheetsRailControlsComponent {
  public readonly actionsTemplate = viewChild<TemplateRef<unknown>>('actions');

  protected readonly app = Application.sheets;
  /** NFR-8: the menu's words. */
  protected readonly S = SHEETS_STRINGS;
  protected readonly popoverHide = new Subject<void>();
  protected readonly importOpen = signal(false);
  /** The chosen file is becoming a workbook: the dialog stays open, busy, until it is open or has failed. */
  protected readonly importBusy = signal(false);
  protected readonly templatesOpen = signal(false);

  private readonly injector = inject(Injector);
  private readonly sheetsService = inject(SheetsService);
  private readonly router = inject(Router);
  /** SBUX-1145: every new workbook made here opens full-window. */
  private readonly openNew = openInEditorFullWindow(this.router, injectFullWindowStart(Application.sheets));
  private readonly toaster = inject(ToasterService);
  private readonly ga = inject(GoogleAnalyticsService);
  private readonly search = inject(AdvancedSearchService);
  /** Provided by Sheets' own app context; absent elsewhere. */
  private readonly sheetsState = inject(SheetsAppStateService, { optional: true });

  /** Gap row 10: in Sheets, a new workbook is listed once its editor closes (new-workbook.ts). */
  private get library(): LibraryView | null {
    return this.sheetsState ? { search: this.search, folders: this.sheetsState } : null;
  }

  /** A new workbook opens in the editor, already created. A failed create has already said so (the service's toast). */
  protected async newBlankWorkbook(): Promise<void> {
    if (this.injector.get(AccessLimitGateService).blocks('createItem', this.injector)) return;
    this.popoverHide.next();
    this.ga.trackEvent('nav-bar', `new-${StitchType.SHEET}`);
    await createAndOpenWorkbook({ sheetsService: this.sheetsService, router: this.router }, blankWorkbook(), {
      toast: true,
      library: this.library,
      open: this.openNew,
    }).catch(() => undefined);
  }

  protected openImport(): void {
    if (this.injector.get(AccessLimitGateService).blocks('createItem', this.injector)) return;
    this.popoverHide.next();
    this.importOpen.set(true);
  }

  protected openTemplates(): void {
    if (this.injector.get(AccessLimitGateService).blocks('createItem', this.injector)) return;
    this.popoverHide.next();
    this.templatesOpen.set(true);
  }

  protected async templateChosen(choice: TemplateChoice): Promise<void> {
    this.templatesOpen.set(false);
    await createFromTemplateAndOpen(
      { sheetsService: this.sheetsService, router: this.router, library: this.library, open: this.openNew },
      choice,
    ).catch(() => undefined);
  }

  /** The dialog closes once the workbook is open, and stays open after a failure, whose toast gives the reason. */
  protected async importChosen(file: File): Promise<void> {
    if (this.importBusy()) return;
    this.importBusy.set(true);
    const opened = await importAndOpenWorkbook(
      { sheetsService: this.sheetsService, router: this.router, toaster: this.toaster, library: this.library },
      file,
      { open: this.openNew },
    );
    this.importBusy.set(false);
    if (opened) this.importOpen.set(false);
  }
}
